import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ResponsiveLogo } from './ThemeLogo';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { apiGet } from '../utils/api';
import {
  Home,
  LayoutDashboard,
  BookTemplate,
  Bot,
  UsersRound,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Folder,
  Layers,
  Loader2,
} from 'lucide-react';

const PROJECT_NAV = [
  { id: 'people', label: 'People & Teams', icon: UsersRound, to: null },
];

export default function AppSidebar({ 
  collapsed, 
  onToggle, 
  boardName,
  workspaceId,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // My Boards dropdown state
  const [boardsOpen, setBoardsOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  const boardsRef = useRef(null);

  // Quick Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // Fetch boards when dropdown opens
  useEffect(() => {
    if (!boardsOpen || boardsLoaded) return;
    let cancelled = false;
    const fetchAllBoards = async () => {
      setBoardsLoading(true);
      try {
        const wsRes = await apiGet('/workspaces');
        if (!wsRes.ok) return;
        const wsList = await wsRes.json();
        const withBoards = await Promise.all(
          wsList.map(async (ws) => {
            try {
              const bRes = await apiGet(`/workspaces/${ws.workspace_id}/boards`);
              const boards = bRes.ok ? await bRes.json() : [];
              return { ...ws, boards };
            } catch { return { ...ws, boards: [] }; }
          })
        );
        if (!cancelled) {
          setWorkspaces(withBoards.filter(ws => ws.boards.length > 0));
          setBoardsLoaded(true);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setBoardsLoading(false); }
    };
    fetchAllBoards();
    return () => { cancelled = true; };
  }, [boardsOpen, boardsLoaded]);

  // Fetch templates on mount
  useEffect(() => {
    if (templatesLoaded) return;
    const fetchTemplates = async () => {
      try {
        const res = await apiGet('/templates');
        if (res.ok) {
          const data = await res.json();
          // Take top 3 by usage_count, then by most recent
          const sorted = data.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
          setTemplates(sorted.slice(0, 3));
          setTemplatesLoaded(true);
        }
      } catch { /* ignore */ }
    };
    fetchTemplates();
  }, [templatesLoaded]);

  // Close boards dropdown on outside click
  useEffect(() => {
    if (!boardsOpen) return;
    const handler = (e) => {
      if (boardsRef.current && !boardsRef.current.contains(e.target)) {
        setBoardsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [boardsOpen]);

  const isActive = (id) => {
    if (id === 'home' && location.pathname === '/dashboard') return true;
    if (id === 'boards' && (location.pathname.startsWith('/workspace') || location.pathname.startsWith('/board'))) return true;
    if (id === 'templates' && location.pathname === '/templates') return true;
    return false;
  };

  const navItemClasses = (active) => `flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200
    ${active 
      ? 'bg-odapto-orange/10 text-odapto-orange font-medium border border-odapto-orange/30' 
      : 'text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent'
    }
    ${collapsed ? 'justify-center px-2' : ''}`;

  const renderNavItem = (id, label, Icon, to, extra) => {
    const active = isActive(id);
    const content = (
      <div className={navItemClasses(active)} data-testid={`sidebar-nav-${id}`}>
        <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-odapto-orange' : ''}`} />
        {!collapsed && <span className="text-sm truncate">{label}</span>}
        {!collapsed && extra}
      </div>
    );

    if (collapsed) {
      return (
        <TooltipProvider key={id} delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              {to ? <Link to={to}>{content}</Link> : content}
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-foreground text-background">{label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return to ? <Link key={id} to={to}>{content}</Link> : <div key={id}>{content}</div>;
  };

  const CATEGORY_ICONS = {
    'Project Management': '📋',
    'Software Development': '💻',
    'Marketing': '📣',
    'Design': '🎨',
    'Canvases': '📊',
  };

  return (
    <div
      className={`h-full flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out shrink-0 ${
        collapsed ? 'w-[60px]' : 'w-[240px]'
      }`}
      data-testid="app-sidebar"
    >
      {/* Logo & Toggle */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 h-14 border-b border-border`}>
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center gap-2">
            <ResponsiveLogo className="w-auto" style={{ height: '2.75rem' }} />
          </Link>
        )}
        <button
          onClick={onToggle}
          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          data-testid="sidebar-toggle-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Core Navigation */}
      <nav className="px-2 space-y-0.5 mt-1">
        {/* Home */}
        {renderNavItem('home', 'Home', Home, '/dashboard')}

        {/* My Boards — with dropdown */}
        <div ref={boardsRef} className="relative">
          <div onClick={() => {
            if (collapsed) { navigate('/dashboard'); return; }
            setBoardsOpen(prev => !prev);
          }}>
            {renderNavItem('boards', 'My Boards', LayoutDashboard, null,
              <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 transition-transform duration-200 ${boardsOpen ? 'rotate-180' : ''}`} />
            )}
          </div>

          {/* Boards Dropdown */}
          {boardsOpen && !collapsed && (
            <div className="mt-1 ml-2 pl-4 border-l-2 border-border space-y-1 max-h-[240px] overflow-y-auto" data-testid="boards-dropdown">
              {boardsLoading && (
                <div className="flex items-center gap-2 px-2 py-2 text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-xs">Loading boards...</span>
                </div>
              )}
              {!boardsLoading && workspaces.map(ws => (
                <div key={ws.workspace_id}>
                  <Link
                    to={`/workspace/${ws.workspace_id}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={() => setBoardsOpen(false)}
                  >
                    <Folder className="w-3.5 h-3.5 shrink-0 text-odapto-teal" />
                    <span className="font-semibold truncate">{ws.name}</span>
                  </Link>
                  {ws.boards.map(board => (
                    <Link
                      key={board.board_id}
                      to={`/board/${board.board_id}`}
                      className={`flex items-center gap-2 px-2 py-1.5 ml-3 rounded-md text-xs transition-colors ${
                        location.pathname === `/board/${board.board_id}`
                          ? 'bg-odapto-orange/10 text-odapto-orange font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      data-testid={`board-quick-link-${board.board_id}`}
                      onClick={() => setBoardsOpen(false)}
                    >
                      <div
                        className="w-3 h-3 rounded-sm shrink-0 border border-white/20"
                        style={{ backgroundColor: board.background || '#3A8B84' }}
                      />
                      <span className="truncate">{board.name}</span>
                    </Link>
                  ))}
                </div>
              ))}
              {!boardsLoading && workspaces.length === 0 && (
                <p className="px-2 py-2 text-xs text-muted-foreground italic">No boards yet</p>
              )}
            </div>
          )}
        </div>

        {/* Templates */}
        {renderNavItem('templates', 'Templates', BookTemplate, '/templates')}

        {/* Strategy Coach */}
        {collapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/strategy-coach">
                  <div className={navItemClasses(location.pathname.startsWith('/strategy-coach'))} data-testid="sidebar-nav-ai-coach">
                    <Bot className="w-5 h-5 shrink-0" />
                  </div>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-foreground text-background">MrCanvas</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Link to="/strategy-coach">
            <div className={navItemClasses(location.pathname.startsWith('/strategy-coach'))} data-testid="sidebar-nav-ai-coach">
              <Bot className="w-5 h-5 shrink-0" />
              <span className="text-sm truncate">MrCanvas</span>
            </div>
          </Link>
        )}
      </nav>

      {/* Current Board Section */}
      {boardName && (
        <div className="mt-4 px-2">
          {!collapsed && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Current Board
            </p>
          )}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-odapto-orange/10 border border-odapto-orange/20 ${collapsed ? 'justify-center px-2' : ''}`}>
            <Sparkles className="w-4 h-4 text-odapto-orange shrink-0" />
            {!collapsed && (
              <span className="text-sm font-semibold text-foreground truncate">{boardName}</span>
            )}
          </div>
          <div className="mt-1.5 space-y-0.5">
            {PROJECT_NAV.map(item => (
              collapsed ? (
                <TooltipProvider key={item.id} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={navItemClasses(false)} data-testid={`sidebar-nav-${item.id}`}>
                        <item.icon className="w-5 h-5 shrink-0" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-foreground text-background">{item.label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div key={item.id} className={navItemClasses(false)} data-testid={`sidebar-nav-${item.id}`}>
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="text-sm truncate">{item.label}</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Quick Templates — live from backend */}
      {!collapsed && (
        <div className="mt-4 px-2 flex-1 overflow-y-auto">
          <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Quick Templates
          </p>
          <div className="space-y-0.5">
            {templates.length === 0 && !templatesLoaded && (
              <div className="flex items-center gap-2 px-3 py-2 text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs">Loading...</span>
              </div>
            )}
            {templates.map(t => (
              <Link
                key={t.board_id}
                to="/templates"
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer transition-colors"
                data-testid={`quick-template-${t.board_id}`}
              >
                <span className="text-sm">{CATEGORY_ICONS[t.category?.name] || '📋'}</span>
                <div className="min-w-0 flex-1">
                  <span className="text-xs truncate block">{t.template_name || t.name}</span>
                  {t.category?.name && (
                    <span className="text-[10px] text-muted-foreground/60 truncate block">{t.category.name}</span>
                  )}
                </div>
                {(t.usage_count || 0) > 0 && (
                  <span className="text-[9px] text-muted-foreground/50 shrink-0">{t.usage_count} uses</span>
                )}
              </Link>
            ))}
            {templatesLoaded && templates.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground italic">No templates yet</p>
            )}
          </div>
          <Link 
            to="/templates"
            className="block px-3 py-2 mt-1 text-xs text-odapto-teal hover:text-odapto-teal/80 font-medium transition-colors"
          >
            Browse All Templates →
          </Link>
        </div>
      )}

      {/* Spacer when collapsed */}
      {collapsed && <div className="flex-1" />}

      {/* Beer Coaster Image */}
      {!collapsed && (
        <div className="mt-auto px-3 pb-4 pt-2">
          <div className="rounded-xl overflow-hidden bg-amber-50 dark:bg-amber-900/15 border border-amber-200/40 dark:border-amber-700/20">
            <img 
              src="/sidebar/beer-coaster.png" 
              alt="Big Ideas Start on a Beer Coaster" 
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      )}

      {/* Collapsed beer coaster */}
      {collapsed && (
        <div className="px-2 pb-3">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/30 cursor-default">
                  <img src="/sidebar/beer-coaster.png" alt="" className="w-6 h-6 object-contain rounded" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-foreground text-background">
                Big Ideas Start on a Beer Coaster
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
